import { DragOverlay, defaultDropAnimationSideEffects } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Icon } from "semantic-ui-react";

import { useCan } from "components";
import { useDeletePinPendingChecklistMutation } from "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal/components/SortableItem/hooks";
import { useChecklistDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";

import type { UniqueIdentifier } from "@dnd-kit/core";
import type { Checklist } from "models";
import type { SortablePinPendingChecklists } from "modules/PendingChecklistsDashboard/components/PinChecklists/components/PinChecklistModal";
import type { CSSProperties } from "react";

type SortableItemProps = {
  item: SortablePinPendingChecklists;
  checklists?: Checklist[];
};

type DragHandleProps = {
  id: UniqueIdentifier;
};

export const SortableOverlay = () => <DragOverlay dropAnimation={{ sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: "0.4" } } }) }} />;

const DragHandle = ({ id }: DragHandleProps) => {
  const canReorderPinPendingChecklist = useCan("reorder", "pin-pending-checklist");
  const { attributes, listeners, setActivatorNodeRef } = useSortable({ id });

  return (
    <button {...attributes} {...listeners} ref={setActivatorNodeRef} className="drag-button" disabled={!canReorderPinPendingChecklist}>
      <Icon className="move" />
    </button>
  );
};

export const SortableItem = ({ item, checklists }: SortableItemProps) => {
  const canDeletePendingChecklist = useCan("delete", "pin-pending-checklist");
  const { isDragging, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const { mutate: deletePinPendingChecklist } = useDeletePinPendingChecklistMutation();
  const { groupedChecklistsDropdownOptions } = useChecklistDropdownOptions(checklists);

  const style: CSSProperties = { opacity: isDragging ? 0.4 : 1, transform: CSS.Translate.toString(transform), transition };
  const infoText = groupedChecklistsDropdownOptions.find(checklist => checklist.value === item.checklist_id)?.infoText;

  return (
    <li ref={setNodeRef} style={style}>
      <div className="input-wrapper">
        <div className="ordering-number">{item.ordering}</div>
        <span>
          {item.checklist_name} {item.vin_prefix && <p className="vin-container"> - VIN #{item.vin_prefix} </p>} {infoText && <p> - {infoText} </p>}
        </span>
        <DragHandle id={item.id} />
      </div>

      <Button basic onClick={() => deletePinPendingChecklist(item)} className="delete-button" icon disabled={!canDeletePendingChecklist}>
        <Icon className="trash red" />
      </Button>
    </li>
  );
};
